import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";

const NewsDetailPage = () => {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const n = await api.newsItem(slug);
        setItem(n);
        const all = await api.news();
        setRelated(all.filter((x) => x.slug !== slug && x.category === n.category).slice(0, 3));
      } catch {
        setItem({ notFound: true });
      }
    })();
  }, [slug]);

  if (!item) return <div className="min-h-[60vh] flex items-center justify-center text-ink-soft">Loading…</div>;
  if (item.notFound) return <div className="min-h-[60vh] flex items-center justify-center text-ink-soft">Story not found.</div>;

  return (
    <article data-testid="news-detail-page" className="pt-8 md:pt-14">
      <div className="max-w-[900px] mx-auto px-5 md:px-8">
        <Link to="/news" data-testid="news-back-link" className="inline-flex items-center gap-2 text-ink-soft hover:text-ink text-[11px] font-mono-editorial uppercase tracking-[0.22em]">
          <ArrowLeft size={14} /> Back to News
        </Link>
        <div className="mt-8 overline">{item.category}</div>
        <h1 className="mt-4 font-display text-ink text-4xl md:text-5xl lg:text-6xl leading-[1.06] tracking-[-0.02em]">{item.title}</h1>
        <div className="mt-6 flex items-center gap-4 text-[11px] font-mono-editorial text-ink-muted uppercase tracking-[0.18em]">
          <span>{item.author}</span><span>·</span>
          <span>{new Date(item.date).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</span><span>·</span>
          <span>{item.read_time}</span>
        </div>
        <div className="mt-10 aspect-[16/9] relative overflow-hidden bg-cream">
          <img src={item.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
        </div>
        <div className="mt-10 prose-editorial">
          <p className="text-xl md:text-2xl text-ink font-display italic leading-snug">{item.summary}</p>
          <p>
            This dispatch reflects our editorial team{"\u2019"}s reading of a genuine, ongoing development across the {item.category.toLowerCase()} landscape. The framing above is deliberately concise; more substantive reporting on the underlying theme is available in our long-form Articles section.
          </p>
          <p>
            We publish news only when our editors are confident that the underlying pattern is durable enough to warrant a reader{"\u2019"}s attention. Fabricated quotations, invented statistics and manufactured urgency are not part of our editorial practice.
          </p>
          <p>
            When you have finished this brief, we invite you to explore the longer treatment of the same theme in the linked article or interview below. Our commitment is to leave you better informed than the news cycle typically allows.
          </p>
        </div>

        {related.length > 0 && (
          <div className="mt-20 pt-10 border-t border-ink-line" data-testid="news-related">
            <div className="overline overline-muted mb-6">Related dispatches</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((r) => (
                <motion.div key={r.id} whileHover={{ y: -3 }} className="editorial-card p-5" data-testid={`related-news-${r.slug}`}>
                  <Link to={`/news/${r.slug}`} className="block">
                    <div className="overline overline-muted mb-3">{r.category}</div>
                    <div className="font-display text-ink text-lg leading-tight">{r.title}</div>
                    <div className="mt-4 inline-flex items-center gap-1 text-ink text-xs">Read <ArrowUpRight size={12} /></div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
    </article>
  );
};

export default NewsDetailPage;
